import React from 'react';
import { motion } from 'framer-motion';
import './Philosophy.css';

const Philosophy = () => {
  const principles = [
    {
      number: '01',
      title: 'The Predgy Manifesto',
      description: 'Predgy isn\'t just a style — it\'s a mindset. We merge the time-honored traditions of prep (Oxford cloth, cashmere, polished leather) with the raw energy of edge (motorcycle jackets, selvedge denim, weathered canvas). The result? A look that\'s simultaneously boardroom-ready and street-smart.',
      visual: 'prep-edge'
    },
    {
      number: '02',
      title: 'Quality Over Quantity',
      description: 'Fast fashion destroys wardrobes and the planet. We champion the opposite: fewer pieces, better made. Full-grain leather develops character. Selvedge denim fades beautifully. Extra-long staple cotton softens with every wash. We want your wardrobe to tell your story.',
      visual: 'quality'
    },
    {
      number: '03',
      title: 'The Jensen Huang Effect',
      description: 'The modern power player doesn\'t wear a suit — he wears a leather jacket. Think Nvidia\'s CEO commanding billion-dollar meetings in motorcycle-ready attire. This is rugged refinement in action: looking like you could close a deal or hit the open road.',
      visual: 'power'
    }
  ];

  return (
    <section id="philosophy" className="philosophy">
      <div className="philosophy-container">
        <motion.div 
          className="philosophy-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="section-eyebrow">Our Philosophy</span>
          <h2 className="section-title">
            Rugged <span className="highlight">Refinement</span>
          </h2>
          <div className="steel-line center"></div>
          <p className="philosophy-intro">
            We believe clothing should work as hard as you do. Every piece should serve 
            a purpose, age gracefully, and make you look like you mean business.
          </p>
        </motion.div>

        <div className="principles-grid">
          {principles.map((principle, index) => (
            <motion.div
              key={principle.number}
              className={`principle-card ${principle.visual}`}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <div className="principle-number">{principle.number}</div>
              <div className="principle-content">
                <h3>{principle.title}</h3>
                <p>{principle.description}</p>
              </div>
              <div className="principle-accent"></div>
            </motion.div>
          ))}
        </div>

        <motion.div 
          className="philosophy-quote"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <blockquote>
            <span className="quote-mark">"</span>
            <p>Dress like you're already the man you want to become.</p>
            <span className="quote-mark closing">"</span>
          </blockquote>
          <cite>— The Slate & Steel Creed</cite>
        </motion.div>

        {/* Quality Materials Showcase */}
        <div className="materials-showcase">
          <motion.h3
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Materials We Champion
          </motion.h3>
          <div className="materials-grid">
            {[
              { name: 'Full-Grain Leather', desc: 'Develops rich patina over time' },
              { name: 'Japanese Selvedge', desc: 'Shuttle-loomed heritage denim' },
              { name: 'ELS Cotton', desc: 'Extra-long staple for supreme softness' },
              { name: 'Cashmere Blends', desc: 'Luxurious warmth without bulk' },
              { name: 'Waxed Canvas', desc: 'Water-resistant with rugged character' },
              { name: 'Shell Cordovan', desc: 'The finest leather for footwear' }
            ].map((material, index) => (
              <motion.div
                key={material.name}
                className="material-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <span className="material-name">{material.name}</span>
                <span className="material-desc">{material.desc}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Background Elements */}
      <div className="philosophy-bg-left"></div>
      <div className="philosophy-bg-right"></div>
    </section>
  );
};

export default Philosophy;





